import { IbcShipTypeEnum } from 'src/enums/stowage/ibcShipTypeEnum';
import { MarpolPollutionCategoryEnum } from 'src/enums/stowage/marpolPollutionCategoryEnum';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';

export enum OptionValueType {
    NA,
    CpWeight,
    CpWeightMinusOption,
    CpWeightPlusOption,
}
export interface OptionConfiguration {
    optionType: string;
    nominatedValueType: OptionValueType;
    minValueType: OptionValueType;
    maxValueType: OptionValueType;
}

export const optionsConfiguration: OptionConfiguration[] = [
    {
        optionType: 'MOLOO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'MOLCO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'MOO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.NA,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'MCO',
        nominatedValueType: OptionValueType.CpWeightPlusOption,
        minValueType: OptionValueType.NA,
        maxValueType: OptionValueType.CpWeightPlusOption,
    },
    {
        optionType: 'LOO',
        nominatedValueType: OptionValueType.CpWeight,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.NA,
    },
    {
        optionType: 'LCO',
        nominatedValueType: OptionValueType.CpWeight,
        minValueType: OptionValueType.CpWeightMinusOption,
        maxValueType: OptionValueType.NA,
    },
    {
        optionType: 'Min / Max',
        nominatedValueType: OptionValueType.CpWeight,
        minValueType: OptionValueType.CpWeight,
        maxValueType: OptionValueType.CpWeight,
    },
    { optionType: '', nominatedValueType: OptionValueType.CpWeight, minValueType: OptionValueType.NA, maxValueType: OptionValueType.NA },
];

export const getOptionConfiguration = (cargo: RotationCargo): OptionConfiguration | null => {
    return optionsConfiguration.find((option) => option.optionType === cargo.optionType) ?? null;
};

const getOptionAdjustement = (cargo: RotationCargo, optionValueType: OptionValueType): number | null => {
    switch (optionValueType) {
        case OptionValueType.NA:
            return null;
        case OptionValueType.CpWeight:
            return 1;
        case OptionValueType.CpWeightMinusOption:
            return 1 + cargo.optionMinusPercentage / 100;
        case OptionValueType.CpWeightPlusOption:
            return 1 + cargo.optionPlusPercentage / 100;
    }
};

export const getNominatedWeighAfterOptionAdjustement = (contractWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.nominatedValueType);
    // TODO: LOGIC-ERROR - Redundant null check: optionConfiguration is already checked above, the ternary condition will always be true. Should simplify to: contractWeight * optionAdjustement
    return optionAdjustement ? contractWeight * (optionConfiguration ? optionAdjustement : 1) : null;
};
export const getContractWeightFromOptionAdjustedNominatedWeight = (nominatedWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.nominatedValueType);
    // TODO: LOGIC-ERROR - Redundant null check: optionConfiguration is already checked above, the ternary condition will always be true. Should simplify to: nominatedWeight / optionAdjustement
    return optionAdjustement ? nominatedWeight / (optionConfiguration ? optionAdjustement : 1) : null;
};
export const getNominatedWeightMinimum = (contractWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.minValueType);
    // TODO: LOGIC-ERROR - Redundant null check: optionConfiguration is already checked above, the ternary condition will always be true. Should simplify to: contractWeight * optionAdjustement
    return optionAdjustement ? contractWeight * (optionConfiguration ? optionAdjustement : 1) : null;
};
export const getNominatedWeightMaximum = (contractWeight: number, cargo: RotationCargo): number | null => {
    const optionConfiguration = getOptionConfiguration(cargo);
    if (!optionConfiguration) return null;
    const optionAdjustement = getOptionAdjustement(cargo, optionConfiguration.maxValueType);
    // TODO: LOGIC-ERROR - Redundant null check: optionConfiguration is already checked above, the ternary condition will always be true. Should simplify to: contractWeight * optionAdjustement
    return optionAdjustement ? contractWeight * (optionConfiguration ? optionAdjustement : 1) : null;
};
export interface SwingValues {
    minWeight: number | null;
    maxWeight: number | null;
}

export const getSwingValues = (contractWeight: number, cargo: RotationCargo): SwingValues => {
    return {
        minWeight: getNominatedWeightMinimum(contractWeight, cargo),
        maxWeight: getNominatedWeightMaximum(contractWeight, cargo),
    };
};

export const getOptionString = (cargo: RotationCargo): string => {
    return `${cargo.optionPlusPercentage}% ${cargo.optionType}`;
};

export const shouldShowOptionMinusValue = (cargo: RotationCargo): boolean => {
    const optionConfiguration = getOptionConfiguration(cargo);
    return optionConfiguration?.minValueType !== OptionValueType.NA;
};

export const shouldShowOptionPlusValue = (cargo: RotationCargo): boolean => {
    const optionConfiguration = getOptionConfiguration(cargo);
    return optionConfiguration?.maxValueType !== OptionValueType.NA;
};

type OperationTypeMap = {
    [key: string]: string;
};

const OPERATION_TYPE_MAP: OperationTypeMap = {
    Loading: 'Loading',
    Unloading: 'Unloading',
    Transit: 'Transit',
    Transfer: 'Transfer',
};

const IBC_SHIP_TYPE_MAP = {
    [IbcShipTypeEnum.Unknown]: '?',
    [IbcShipTypeEnum.One]: '1',
    [IbcShipTypeEnum.Two]: '2',
    [IbcShipTypeEnum.TwoK]: '2k',
    [IbcShipTypeEnum.Three]: '3',
};

const MARPOL_POLLUTION_MAP = {
    [MarpolPollutionCategoryEnum.Unknown]: '?',
    [MarpolPollutionCategoryEnum.X]: 'X',
    [MarpolPollutionCategoryEnum.Y]: 'Y',
    [MarpolPollutionCategoryEnum.Z]: 'Z',
    [MarpolPollutionCategoryEnum.OS]: 'OS',
};

export const getOperationType = (opType: string) => OPERATION_TYPE_MAP[opType] || '';
export const getIbcShipTypeString = (shipType: IbcShipTypeEnum) => IBC_SHIP_TYPE_MAP[shipType] || '';
export const getMarpolPollutionCategoryString = (pollutionType: MarpolPollutionCategoryEnum) => MARPOL_POLLUTION_MAP[pollutionType] || '';
